import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Popover } from '@brighterion/br-ui';
import { handleKeyEvent, handleOnClickEvent } from 'Utils';
import { useHistory } from 'react-router-dom';
import useGetLandlordInfo from 'Hooks/query-hooks/tenant/useGetLandlordInfo';
import { Formik } from 'formik';
import { RoleService } from 'Services';
import ButtonGroup from 'Components/ButtonGroup';
import PageHeader from 'Components/PageHeader';
import DeleteRuleModal from 'Components/DeleteRuleModal/DeleteRuleModal';
import AddRuleInfoOverview from 'Components/AddRulePage/AddRuleInfoOverview';
import { trackEvent, EventTypes, EventNames } from '@brighterion/insights';
import { shouldDeleteRule, getUser } from 'Selectors';
import ScriptOverview from '../ScriptOverview';
import { advancedRuleValidationSchema } from './advancedRuleSchema';

import styles from './AdvancedRuleForm.css';

type AdvancedRuleFormProps = {
    parameterData: UseGetParametersResponse | undefined;
    disabledInputFields: DisabledInputFields;
    formInitialValues: AdvancedRuleFormSchema;
    formRef?: any;
    listData: ListApiGetResponse;
    mode: 'add' | 'edit';
    mutation: any;
    pageHeader: string;
    repoId: number;
    selectedRowId?: string | string[] | null;
    disableDeleteRuleButton?: boolean;
    isNonTemplatedRule?: boolean;
    isRuleDeployed?: boolean;
};

const AdvancedRuleForm = ({
    parameterData,
    disabledInputFields,
    formInitialValues,
    formRef,
    listData,
    mode,
    mutation,
    pageHeader,
    repoId,
    selectedRowId,
    disableDeleteRuleButton,
    isNonTemplatedRule,
    isRuleDeployed
}: AdvancedRuleFormProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [isOpen, setIsOpen] = React.useState(false);
    const { data: landlord, isLoading: landlordIsLoading } = useGetLandlordInfo();
    const { landlordId, tenantKey } = useSelector(getUser);
    const isImpersonationMode = Boolean(landlordId) && landlordId !== tenantKey;
    const showDeleteRuleButton = isNonTemplatedRule
        ? mode === 'edit' && RoleService.hasAdvancedRuleDeleteAccess && shouldDeleteRule()
        : false;

    if (landlordIsLoading) return null;

    const handleCancel = () => {
        const params = selectedRowId ? `?selectedRowId=${selectedRowId}` : '';

        history.push({
            pathname: '/global-rules',
            search: params
        });
    };
    const handleTrackEvent = () => {
        let clickIdentifier = '';
        let text = '';
        if (mode === 'add') {
            clickIdentifier = 'Global Rules - Add New Advanced Rule';
            text = 'Add New Advanced Rule';
            if (isImpersonationMode) {
                clickIdentifier = 'Global Rules - Add New Advanced Tenant Rule';
                text = 'Add New Advanced Tenant Rule';
            }
        } else if (mode === 'edit') {
            clickIdentifier = 'Global Rules - Edit Advanced Rule';
            text = 'Edit Advanced Rule';
            if (isImpersonationMode) {
                clickIdentifier = 'Global Rules - Edit Advanced Tenant Rule';
                text = 'Edit Advanced Tenant Rule Script';
            }
        }
        if (!clickIdentifier || !text) {
            return;
        }

        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier,
                text
            }
        });
    };

    const handleFormSubmit = (formValues: AdvancedRuleFormSchema) => {
        const {
            ruleId,
            group,
            active,
            description,
            expression,
            allowOverrides,
            comment,
            priority,
            hiddenFromTenant
        } = formValues;
        /**
         * @note Rule endpoint expects `conditions` and `actions` to be an empty Array.
         * Because Advanced Rule never GET the initial values it is added here.
         */
        const mergeRuleValues = {
            conditions: [],
            actions: []
        };

        mutation.mutate({
            ...mergeRuleValues,
            active,
            allowOverrides,
            comment: { [ruleId]: comment?.trim() },
            description,
            expression,
            group: parseInt(group.toString(), 10),
            level: 'ACQUIRER',
            priority,
            ruleId,
            templateRepositoryId: repoId,
            hiddenFromTenant
        });

        handleTrackEvent();
    };

    /**
     * @note Read-only script editor if:
     * - The user is a Threshold Editor (only) and edit the Advanced Rule, but can only change the Theshold `status`,
     * - The user is a Landlord, Allowed Overrides and Deployed the Advanced Rule.
     */
    let readOnlyScriptEditor = false;

    if (mode === 'edit' && RoleService.isThresholdEditor() && RoleService.roles.length === 1) {
        readOnlyScriptEditor = true;
        disabledInputFields.disableOverrideChange = true;
    }

    if (landlord?.isLandlord && disabledInputFields.disableDefinitionChange) {
        readOnlyScriptEditor = true;
    }

    return (
        <Formik
            enableReinitialize
            initialValues={formInitialValues}
            onSubmit={(formValues) => handleFormSubmit(formValues)}
            validationSchema={advancedRuleValidationSchema(t)}
            innerRef={formRef}
        >
            {(formikProps) => {
                const { dirty, isValid, isSubmitting, handleSubmit, values, initialValues } = formikProps;
                const disableSave = !dirty || !isValid;

                // disable comment field until at least one other form value is modified
                const disableCommentField = values.comment === initialValues.comment && !dirty;

                const submitForm = () => {
                    handleSubmit();
                };

                const toggleModal = () => {
                    setIsOpen(!isOpen);
                };

                return (
                    <>
                        <PageHeader>
                            <PageHeader.Left className={styles.titleText}>{pageHeader}</PageHeader.Left>
                            <PageHeader.Right className={styles.rightButtonContainer}>
                                <ButtonGroup direction='row'>
                                    {showDeleteRuleButton && (
                                        <>
                                            <div>
                                                <Popover
                                                    position='top-end'
                                                    theme='light'
                                                    showArrow
                                                    showOnHover={disableDeleteRuleButton}
                                                    isControlled={!disableDeleteRuleButton}
                                                    popperClassName={styles.popper}
                                                    content={t('disableDeleteRuleButtonTooltip')}
                                                    contentClassName={styles.disableDeleteRuleTooltip}
                                                    arrowClassName={styles.disableDeleteRulePopperArrowStyle}
                                                >
                                                    <Button
                                                        className={styles.deleteRuleButton}
                                                        disabled={isSubmitting || disableDeleteRuleButton}
                                                        iconName='delete'
                                                        dataTestId='advanced-rule-form-delete-button'
                                                        variant='tertiary'
                                                        onKeyDown={handleKeyEvent(toggleModal)}
                                                        onClick={handleOnClickEvent(toggleModal)}
                                                    >
                                                        {t('Delete Rule')}
                                                    </Button>
                                                </Popover>
                                            </div>
                                            <hr className={styles.separator} />
                                        </>
                                    )}
                                    <Button
                                        dataTestId='advanced-rule-form-cancel-button'
                                        disabled={isSubmitting}
                                        onKeyDown={handleKeyEvent(handleCancel)}
                                        onClick={handleOnClickEvent(handleCancel)}
                                        size='medium'
                                        variant='tertiary'
                                    >
                                        {t('Cancel')}
                                    </Button>
                                    <Button
                                        dataTestId='advanced-rule-form-save-button'
                                        disabled={disableSave || isSubmitting}
                                        onKeyDown={handleKeyEvent(submitForm)}
                                        onClick={handleOnClickEvent(submitForm)}
                                        size='small'
                                        variant='primary'
                                    >
                                        {isSubmitting ? t('Saving...') : t('Save Rule')}
                                    </Button>
                                </ButtonGroup>
                            </PageHeader.Right>
                        </PageHeader>
                        <AddRuleInfoOverview
                            mode={mode}
                            ruleType='advanced'
                            listData={listData.result}
                            selectedRowId={selectedRowId}
                            disabledInputFields={disabledInputFields}
                        />
                        <DeleteRuleModal
                            templateRepositoryId={repoId}
                            ruleId={formInitialValues.ruleId}
                            isOpen={isOpen}
                            closeModal={() => setIsOpen(false)}
                            isRuleDeployed={!!isRuleDeployed}
                        />
                        {/** @note Rule Script Overview get all props from useFormikContext()  */}
                        <ScriptOverview
                            scriptType='rule'
                            inputFields={[
                                {
                                    name: 'comment',
                                    label: 'Comment (Optional)',
                                    className: styles.comment,
                                    disabled: disableCommentField || isSubmitting
                                }
                            ]}
                            leftTitle={t('Rule Statement')}
                            parameterData={parameterData}
                            listData={listData.result}
                            readOnly={readOnlyScriptEditor}
                        />
                    </>
                );
            }}
        </Formik>
    );
};

export default AdvancedRuleForm;
